import { Divider, Flex, Image } from "@chakra-ui/react";
import { Trans, useTranslation } from "next-i18next";

import useProductLocalization from "components/hooks/Localization/useProductLocalization";
import { OFIconButton } from "design_system/components/IconButton";
import { OFText } from "design_system/components/OFText";
import { TrashcanIcon } from "design_system/icons";
import { CalculateOrderSummaryModel, StripeProductLocaleKeys } from "graphql/generated";
import { colors } from "src/colors";
import { Evaluations } from "src/configs/evaluations";
import { useConfig } from "src/hooks";
import { formatPrice } from "src/utils/priceFormat";

import { summaryItemImageProps } from "./OrderSummaryItemHeader";
import { OrderSummaryItemWrapper } from "./OrderSummaryItemWrapper";
import { PRODUCT_NAME_STYLE } from "./constant/style";

type Props = {
  platform: string;
  item: any;
  onOpenCancelModal: (item: any) => void;
  allowAction?: boolean;
  summary: CalculateOrderSummaryModel;
};

export const OTCItemTeleConsultFee = ({
  onOpenCancelModal,
  item,
  platform,
  allowAction,
  summary
}: Props) => {
  const { getTranslatedText } = useProductLocalization();
  const { country, addSuffixCountry } = useConfig();
  const { price, product, quantity } = item;
  const { id } = product;
  const evalId = item.teleConsultForSkipEval || item.completedEval?.evalId;
  if (!evalId) {
    throw new Error("Invalid eval ID");
  }
  const evalTransKey = addSuffixCountry("eval");
  const { t: evalTranslator } = useTranslation([evalTransKey]);
  const { t } = useTranslation();

  const evalInfo = Evaluations[country][platform].find((i) => i.code === evalId);
  const priceItemSummary = summary?.itemList?.find((it) => it?.priceId === price?.id);

  return (
    <OrderSummaryItemWrapper key={item.price.id}>
      <Flex mb="16px">
        <Image
          {...summaryItemImageProps}
          src={`/assets/icons/${evalInfo.icon}.svg`}
          borderRadius="8px"
          objectFit="cover"
        />
        <Flex justifyContent="space-between" flex={1}>
          <Flex ml={["16px"]} flexDirection="column">
            <OFText textStyle={PRODUCT_NAME_STYLE}>
              <Trans components={{ br: <br /> }}>
                {t(`${addSuffixCountry("checkout")}:skip-med-treatment`, {
                  evaluation: evalTranslator(evalInfo.code)
                })}
              </Trans>
            </OFText>
            <OFText>
              <OFText display="inline" textStyle="body2">
                {getTranslatedText(id, StripeProductLocaleKeys.QuantityPerUnit)}
              </OFText>
            </OFText>
          </Flex>
        </Flex>
      </Flex>
      <Flex justifyContent="space-between" mb="16px">
        <OFText textStyle="body2">
          {`${getTranslatedText(id, StripeProductLocaleKeys.ProductName)}${
            quantity > 1 ? ` x${quantity}` : ""
          }`}
        </OFText>

        <OFText textStyle="body2semibold" color={colors.grey[700]}>
          {formatPrice(priceItemSummary?.itemSubTotalAfterDiscount || 0)}
        </OFText>
      </Flex>

      {allowAction && (
        <>
          <Divider m="16px 0px 8px 0px" borderColor={colors.grey[500]} />

          <Flex w="100%" justifyContent="flex-end" gap="16px">
            <OFIconButton icon={<TrashcanIcon />} onClick={() => onOpenCancelModal(item)} />
          </Flex>
        </>
      )}
    </OrderSummaryItemWrapper>
  );
};
